import { apiFetch } from './client'

/**
 * s63 / ADR-0033 fatia 1 -- assistente "Pergunte ao sistema", NIVEL 1 (sem IA).
 * O servidor monta a resposta do banco e devolve a ORIGEM; a tela so mostra (nenhum texto e montado aqui).
 */
export interface AssistantTopic {
  id: string
  question: string
  needs: 'job'
}

export interface AssistantPerson {
  id: string
  name: string
}

export interface AssistantAnswer {
  topic: string
  outcome: 'answered' | 'no_published'
  job_id: string
  job_name: string
  question: string
  lines: string[]
  source: string
  can_open_job: boolean
  people_count: number | null
  people: AssistantPerson[] | null
  /** s64: 1 = menus (sem IA); 2 = a IA entendeu a pergunta escrita (so ai a tela mostra a VIGA, D9). */
  level?: number
  /** s64 / 2d: de QUEM e a resposta -- 'cargo' (job_id), 'pessoa' ou 'area' (job_name = o nome dela). */
  subject_type?: 'cargo' | 'pessoa' | 'area'
  subject_id?: string | null
  /** s64 / 2e (R1): a resposta numa frase montada pelo SERVIDOR (modelo fixo por assunto; a IA nao escreve). */
  phrase?: string | null
  /** false = a frase ja diz tudo (as linhas nao aparecem); ``lines_from`` = a frase ja disse as primeiras linhas. */
  show_lines?: boolean
  lines_from?: number
  /** s64 / 2e (R2): proximas perguntas oferecidas como botao. */
  suggestions?: AssistantSuggestion[]
  /** s65 / 2g (G2 A): o fecho de continuidade da fala, montado pelo servidor (5 jeitos, pela vez). */
  closing?: string | null
  /** s66 / 2h (H1): a frase com o nome de quem pergunta -- so na 1a resposta da conversa escrita. */
  phrase_named?: string | null
}

export interface AssistantSuggestion {
  topic: string
  label: string
}

export const ASSISTANT_TOPICS_KEY = ['assistant', 'topics'] as const

export function fetchAssistantTopics(): Promise<AssistantTopic[]> {
  return apiFetch<AssistantTopic[]>('/api/assistant/topics')
}

export function askAssistant(topic: string, jobId: string): Promise<AssistantAnswer> {
  return apiFetch<AssistantAnswer>('/api/assistant/answers', { method: 'POST', body: { topic, job_id: jobId } })
}

/** s64 / 2e (R2): o botao de proxima pergunta -- mesmo assunto-por-id do menu, sobre o cargo, a pessoa ou a area. */
export function askAssistantNext(topic: string, sobre: AssistantAnswer, turn = 0): Promise<AssistantAnswer> {
  const id = sobre.subject_id ?? ''
  const alvo =
    sobre.subject_type === 'pessoa'
      ? { person_id: id }
      : sobre.subject_type === 'area'
        ? { area_id: id }
        : { job_id: sobre.job_id }
  const body = { topic, ...alvo, ...(turn > 0 ? { turn } : {}) }
  return apiFetch<AssistantAnswer>('/api/assistant/answers', { method: 'POST', body })
}

/**
 * s64 / ADR-0033 fatia 2 -- pergunta ESCRITA (nivel 2). O servidor mascara o texto, a IA so diz o assunto e o cargo, e a
 * resposta vem do MESMO motor do nivel 1. ``message`` e texto FIXO do sistema (nunca do provedor).
 */
export interface AssistantQuestionResult {
  outcome: string
  level: number
  message: string | null
  candidates: string[]
  /** s64 / 2c (C5): ate 3 respostas numa pergunta ("papel e formacao do X"). */
  answers: AssistantAnswer[]
  /** s70 / polegar (ADR-0036 L2): o id do registro desta pergunta -- volta no 👍/👎. */
  query_id?: string | null
}

/** s64 / 2c (C1): o que a TELA lembra da conversa -- so ids (o ultimo assunto e o ultimo cargo respondidos). */
export interface AssistantContext {
  topic: string
  job_id?: string
  person_id?: string
  area_id?: string
}

/** s64 / 2f: ``turn`` = a vez na conversa (o servidor varia o jeito de dizer); so vai quando > 0. */
export function askAssistantText(
  text: string,
  context: AssistantContext | null,
  turn = 0,
): Promise<AssistantQuestionResult> {
  return apiFetch<AssistantQuestionResult>('/api/assistant/questions', {
    method: 'POST',
    body: { text, ...(context ? { context } : {}), ...(turn > 0 ? { turn } : {}) },
  })
}

/**
 * s70 / polegar (ADR-0036 L2; Marcelo 30/10/2026 19:55, F1-F6): 👍/👎 OPCIONAL de quem perguntou; vale o ultimo voto.
 * "O que faltou?" so no 👎, ate 280 caracteres; o servidor mascara antes de gravar e o texto nunca vai a IA.
 */
export function rateAssistantAnswer(queryId: string, rating: 'up' | 'down', missing?: string): Promise<{ rating: string; saved: boolean }> {
  return apiFetch<{ rating: string; saved: boolean }>(`/api/assistant/questions/${encodeURIComponent(queryId)}/feedback`, {
    method: 'POST',
    body: { rating, ...(missing && missing.trim() ? { missing: missing.trim().slice(0, 280) } : {}) },
  })
}
